import { i18n } from "@/plugins/vue-i18n";

const state = () => ({
  migrationsHistory: [],
  migrationDetails: {},
  loadingMigrationsHistory: false,
});

function createGettersForNewState(newState) {
  return Object.fromEntries(
    Object.keys(newState).map((key) => [key, (state) => state[key]])
  );
}

function createSetterMutationsForNewState(newState) {
  return Object.fromEntries(
    Object.keys(newState).map((key) => {
      return [key, (state, value) => (state[key] = value)];
    })
  );
}

function sortMigrationsByDate(migrations) {
  return migrations
    .map((item) => ({
      ...item,
      created_at:
        typeof item.created_at === "number"
          ? item.created_at
          : Date.parse(item.created_at) || 0,
    }))
    .sort((a, b) => b.created_at - a.created_at);
}

const getters = {
  ...createGettersForNewState(state()),
};

const mutations = {
  ...createSetterMutationsForNewState(state()),
  pushMigrationsHistory(state, migrations) {
    state.migrationsHistory = sortMigrationsByDate([
      ...state.migrationsHistory,
      ...migrations,
    ]);
  },
  updateMigrationHistory(state, { key, data }) {
    state.migrationsHistory = state.migrationsHistory.map((migration) => {
      if (migration.migration_key === key) {
        migration.status = data.status;
      }
      return migration;
    });
  },
  setMigrationDetails(state, { id, data }) {
    state.migrationDetails = {
      ...state.migrationDetails,
      [id]: data,
    };
  },
};

const actions = {
  async syncMigrationsHistory({ getters, commit }) {
    commit("loadingMigrationsHistory", true);

    const url = `${process.env.VUE_APP_API_BASE_URL}/migration`;

    const config = {
      headers: { Authorization: this._vm.token },
    };

    try {
      const response = await this._vm.$axios.get(url, config);
      commit("migrationsHistory", sortMigrationsByDate(response.data));
    } catch (error) {
      console.error("Vuex: getMigrationsHistory()", error);
    } finally {
      commit("loadingMigrationsHistory", false);
    }

    return getters.migrationsHistory;
  },

  async getMigrationsHistory({ getters, dispatch }) {
    if (getters.migrationsHistory.length) {
      return getters.migrationsHistory;
    }

    return await dispatch("syncMigrationsHistory");
  },

  async batchtMigrations({ commit }, payload) {
    const url = `${process.env.VUE_APP_API_BASE_URL}/migration/batch`;

    const config = {
      headers: { Authorization: this._vm.token },
    };

    try {
      const response = await this._vm.$axios.post(url, payload, config);
      commit("pushMigrationsHistory", response.data);
      commit("setSnackBar", {
        message: i18n.t("confirmMigrationModal.successSnack"),
        show: true,
      });
    } catch (error) {
      console.error("Vuex: batchtMigrations()", error);
      if (error?.response?.data?.code === "INVALID_GOOGLE_ACCESS_TOKEN") {
        commit("setSnackBar", {
          message: i18n.t("errorMessages.INVALID_GOOGLE_ACCESS_TOKEN"),
          show: true,
          color: "error",
        });
      } else {
        commit("setSnackBar", {
          message: i18n.t("confirmMigrationModal.errorSnack"),
          show: true,
          color: "error",
        });
      }
      return Promise.reject(false);
    }

    return Promise.resolve(true);
  },

  async getMigrationDetails({ commit }, migration_key) {
    const url = `${process.env.VUE_APP_API_BASE_URL}/migration/${migration_key}`;
    const config = { headers: { Authorization: this._vm.token } };

    try {
      const response = await this._vm.$axios.get(url, config);

      if (response.data && Object.keys(response.data).length > 0) {
        commit("setMigrationDetails", {
          id: migration_key,
          data: response.data,
        });
        commit("updateMigrationHistory", {
          key: migration_key,
          data: response.data,
        });
      }
    } catch (error) {
      console.error(`Vuex: getMigrationDetails(${migration_key})`, error);
    }
  },

  async cancelMigration({ commit, getters }, migrationKey) {
    commit("loadingMigrationsHistory", true);

    const url = `${process.env.VUE_APP_API_BASE_URL}/migration/stop`;

    const config = { headers: { Authorization: this._vm.token } };

    const payload = {
      migration_key: migrationKey,
    };

    try {
      const response = await this._vm.$axios.put(url, payload, config);

      if (response.data && Object.keys(response.data).length > 0) {
        const migration = response.data;

        const newHistory = getters.migrationsHistory.map((item) => {
          if (item.migration_key === migrationKey) {
            return migration;
          }
          return item;
        });

        commit("migrationsHistory", newHistory);

        let message = i18n.t("cancelMigrationDialog.successfullyCanceled"); // valor default por enquanto
        if (migration.status === "CANCELED") {
          message = i18n.t("cancelMigrationDialog.successfullyCanceled");
        } else if (migration.status === "INTERRUPTED") {
          message = i18n.t("cancelMigrationDialog.successfullyInterrupted");
        }
        commit("setSnackBar", {
          message,
          show: true,
        });
      }
    } catch (error) {
      console.error(`Vuex: cancelMigration(${migrationKey})`, error);
      commit("setSnackBar", {
        message: i18n.t("cancelMigrationDialog.cancelFailed"),
        show: true,
        color: "error",
      });
    } finally {
      commit("loadingMigrationsHistory", false);
    }

    return Promise.resolve();
  },
};

export default {
  namespaced: false,
  state,
  getters,
  mutations,
  actions,
};
